import React, { useState } from "react";
import { X, Plus } from "lucide-react";

const VIEWS = [
  { id: "top", label: "Top view" },
  { id: "front", label: "Front" },
  { id: "rear", label: "Rear" },
  { id: "left", label: "Left side" },
  { id: "right", label: "Right side" },
];

// Simple stylised SVG outlines for each view (no external assets)
const TopSilhouette = () => (
  <g>
    {/* body */}
    <rect x="80" y="30" width="240" height="140" rx="50" ry="50" fill="#edf2f4" stroke="#9aa6ad" strokeWidth="2" />
    {/* roof */}
    <rect x="120" y="60" width="160" height="80" rx="20" ry="20" fill="#cfd7db" stroke="#9aa6ad" />
    {/* bonnet/boot split lines */}
    <line x1="120" y1="100" x2="280" y2="100" stroke="#9aa6ad" strokeDasharray="3 3" />
    {/* wheels */}
    <rect x="65" y="55" width="20" height="34" rx="4" fill="#20282c" />
    <rect x="315" y="55" width="20" height="34" rx="4" fill="#20282c" />
    <rect x="65" y="111" width="20" height="34" rx="4" fill="#20282c" />
    <rect x="315" y="111" width="20" height="34" rx="4" fill="#20282c" />
  </g>
);
const FrontSilhouette = () => (
  <g>
    <rect x="60" y="60" width="280" height="100" rx="14" fill="#edf2f4" stroke="#9aa6ad" strokeWidth="2" />
    <rect x="90" y="75" width="220" height="50" rx="8" fill="#cfd7db" stroke="#9aa6ad" />
    <circle cx="100" cy="135" r="10" fill="#fff" stroke="#9aa6ad" />
    <circle cx="300" cy="135" r="10" fill="#fff" stroke="#9aa6ad" />
    <rect x="180" y="145" width="40" height="10" rx="2" fill="#20282c" />
  </g>
);
const SideSilhouette = ({ mirror = false }) => (
  <g transform={mirror ? "translate(400 0) scale(-1 1)" : ""}>
    <path d="M40,140 L60,90 L120,70 L240,70 L300,100 L360,110 L360,140 Z" fill="#edf2f4" stroke="#9aa6ad" strokeWidth="2" />
    <rect x="80" y="80" width="60" height="40" rx="4" fill="#cfd7db" stroke="#9aa6ad" />
    <rect x="150" y="80" width="80" height="40" rx="4" fill="#cfd7db" stroke="#9aa6ad" />
    <circle cx="100" cy="142" r="18" fill="#20282c" />
    <circle cx="300" cy="142" r="18" fill="#20282c" />
  </g>
);

const renderView = (v) => {
  if (v === "top") return <TopSilhouette />;
  if (v === "front" || v === "rear") return <FrontSilhouette />;
  if (v === "left") return <SideSilhouette />;
  if (v === "right") return <SideSilhouette mirror />;
  return null;
};

export const DamageDiagram = ({ damages = [], onChange, testid }) => {
  const [view, setView] = useState("top");
  const [activeMarker, setActiveMarker] = useState(null);
  const [draftLabel, setDraftLabel] = useState("");

  const onSvgClick = (e) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 400;
    const y = ((e.clientY - rect.top) / rect.height) * 200;
    const id = Math.random().toString(36).slice(2, 9);
    const marker = { id, view, x, y, label: "" };
    onChange([...damages, marker]);
    setActiveMarker(id);
    setDraftLabel("");
  };

  const updateLabel = (id, label) => {
    onChange(damages.map((d) => (d.id === id ? { ...d, label } : d)));
  };

  const removeMarker = (id) => {
    onChange(damages.filter((d) => d.id !== id));
    setActiveMarker(null);
  };

  const currentMarkers = damages.filter((d) => d.view === view);

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="font-display uppercase tracking-widest text-[11px] text-[#3f4b52]">
          Damage diagram · tap to add markers
        </div>
        <div className="flex flex-wrap gap-1.5">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setView(v.id)}
              className={`px-2.5 py-1 rounded-sm font-display uppercase tracking-wider text-xs ${
                view === v.id ? "bg-[#f28a16] text-white" : "bg-[#edf2f4] text-[#3f4b52]"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-[#cfd7db] rounded-md bg-white">
        <svg
          viewBox="0 0 400 200"
          onClick={onSvgClick}
          className="w-full h-auto cursor-crosshair select-none"
          data-testid="damage-svg"
        >
          {renderView(view)}
          {currentMarkers.map((m, i) => (
            <g key={m.id} onClick={(e) => { e.stopPropagation(); setActiveMarker(m.id); setDraftLabel(m.label || ""); }}>
              <circle cx={m.x} cy={m.y} r="9" fill="#dc2626" stroke="#fff" strokeWidth="2.5" />
              <text x={m.x} y={m.y + 3.5} textAnchor="middle" fontSize="10" fontWeight="700" fill="#fff">
                {i + 1}
              </text>
            </g>
          ))}
        </svg>
      </div>

      {/* Markers list for the current view */}
      {currentMarkers.length > 0 && (
        <ul className="mt-3 space-y-2">
          {currentMarkers.map((m, i) => (
            <li key={m.id} className="bg-white border border-[#cfd7db] rounded-md p-2.5 flex items-center gap-2">
              <span className="h-6 w-6 rounded-full bg-[#dc2626] text-white text-xs font-bold flex items-center justify-center shrink-0">
                {i + 1}
              </span>
              <input
                type="text"
                value={m.label || ""}
                onChange={(e) => updateLabel(m.id, e.target.value)}
                placeholder="Describe the damage (e.g. scratch left fender)"
                className="flex-1 bg-transparent text-sm focus:outline-none"
              />
              <button
                type="button"
                onClick={() => removeMarker(m.id)}
                className="text-[#dc2626] hover:bg-[#fee2e2] h-7 w-7 rounded-full flex items-center justify-center"
                aria-label="Remove marker"
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default DamageDiagram;
